import { inject, Injectable } from '@angular/core';
import { Pagination, SupabaseQueryParams } from '@app/shared/utils/interfaces';
import { ArrayService } from '@app/shared/utils/services';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { take } from 'rxjs/internal/operators/take';
import { tap } from 'rxjs/internal/operators/tap';

import { Product } from '../interfaces';
import { ProductsActions } from './products.actions';
import { ProductsSelectors } from './products.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProductsFacade {
  private readonly store = inject(Store);
  private readonly arrayService = inject(ArrayService);

  readonly productsLimit = 30;

  readonly allProducts$ = this.store.select(ProductsSelectors.getAllProducts);
  readonly productsEntities$ = this.store.select(ProductsSelectors.getProductsEntities);
  readonly isLoading$ = this.store.select(ProductsSelectors.getProductsLoading);
  readonly isLoaded$ = this.store.select(ProductsSelectors.getProductsLoaded);
  readonly selectedProduct$ = this.store.select(ProductsSelectors.getSelected);
  readonly pagination$ = this.store.select(ProductsSelectors.getPagination);
  readonly queryFilters$ = this.store.select(ProductsSelectors.getQueryFilters);
  readonly sizes$ = this.store.select(ProductsSelectors.getSizes);

  readonly supabaseQueryParams$ = this.store
    .select(ProductsSelectors.getSupabaseQueryParams)
    .pipe(distinctUntilChanged((previous, current) => this.arrayService.isEqual(previous, current)));
  readonly total$ = this.store.select(ProductsSelectors.getTotal);

  loadProducts(queryParams: SupabaseQueryParams) {
    this.store.dispatch(ProductsActions.loadProducts({ queryParams }));
  }

  loadMoreProducts(pagination: Pagination) {
    combineLatest([this.supabaseQueryParams$, this.total$])
      .pipe(
        take(1),
        tap(([queryParams, total]) => {
          if (this.isLastPage(queryParams, total)) return;
          this.store.dispatch(
            ProductsActions.loadMoreProducts({
              queryParams: {
                ...queryParams,
                ...pagination,
              },
            }),
          );
        }),
      )
      .subscribe();
  }

  loadProduct(id: Product['id']) {
    this.store.dispatch(ProductsActions.loadProduct({ id }));
  }

  loadSizes() {
    this.store.dispatch(ProductsActions.loadSizes());
  }

  setSelected(id: Product['id']) {
    this.store.dispatch(ProductsActions.setSelectedProduct({ id }));
  }

  setSupabaseQueryParams(queryParams: SupabaseQueryParams) {
    this.store.dispatch(ProductsActions.setSupabaseQueryParams({ queryParams }));
  }

  setPagination(pagination: Pagination) {
    this.store.dispatch(ProductsActions.setPagination({ pagination }));
  }

  private isLastPage({ currentPage, itemsPerPage }: SupabaseQueryParams, total: number): boolean {
    if (currentPage === 1) return false;
    const startIndex = currentPage * itemsPerPage - itemsPerPage;
    if (startIndex >= total) return true;
    return false;
  }
}
